import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { find } from "lodash";
import { sessionService } from "@/app/providers/services";
import { useRelease } from "@/common";
import DataTable, { type DataTableColumn } from "@imminently/decisively-ui/DataTable";
import Chip from "@imminently/decisively-ui/Chip";
import Icon from "@imminently/decisively-ui/Icon";
import Tooltip from "@imminently/decisively-ui/Tooltip";

const StatusCell = ({ value }: { value?: string }) => {
  const { t } = useTranslation();
  const label = value ?? "in-progress";
  return (
    <Chip backgroundColor="purpleSlate">
      {t(`tasks.status.${label}`)}
    </Chip>
  );
};

export const InteractionTable = ({ sessionId, releaseId }: { sessionId: string; releaseId?: string }) => {
  const { t } = useTranslation();
  const release = useRelease(releaseId);
  const { data, isError, isLoading } = sessionService.useGetOne(sessionId);

  if (isError) return <div>Error...</div>;

  const ruleGraph = release?.rule_graph;
  const interactions = data?.interactions || [];

  const columns: DataTableColumn[] = [
    {
      field: "mode",
      headerName: "Interaction",
      minWidth: 140,
      renderCell: (params) => {
        switch (params.row.mode) {
          case "interview":
            return "Interview";
          case "api":
            return "API";
          case "generative":
            return "Generative Chat";
          default:
            return "Interaction";
        }
      },
    },
    {
      field: "created",
      headerName: "Last Updated",
      minWidth: 130,
      renderCell: (params) => (
        <Tooltip>
          <Tooltip.Trigger asChild>
            <span>{moment(params.row.lastModified).fromNow()}</span>
          </Tooltip.Trigger>
          <Tooltip.Content>{`Created: ${params.row.created}`}</Tooltip.Content>
        </Tooltip>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        let icon;
        switch (params.row.mode) {
          case "interview":
            return (
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <span>
                    <Chip size="sm">
                      <Chip.Icon>
                        <Icon name="show-data" />
                      </Chip.Icon>
                      <Chip.Label>{params.row.interviewName}</Chip.Label>
                    </Chip>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Content>Interview</Tooltip.Content>
              </Tooltip>
            );
          case "generative":
            icon = <Icon name="message" />;
            break;
          default:
            icon = <Icon name="settings" />;
            break;
        }

        const node = ruleGraph?.nodes ? find(ruleGraph.nodes, { v: params.row.goal }) : null;
        if (!node) return params.row.mode;

        let desc = node.value.description;
        if (desc.length > 32) desc = `${desc.slice(0, 32)}...`;
        return (
          <Tooltip>
            <Tooltip.Trigger asChild>
              <span>
                <Chip size="sm">
                  <Chip.Icon>{icon}</Chip.Icon>
                  <Chip.Label>{desc}</Chip.Label>
                </Chip>
              </span>
            </Tooltip.Trigger>
            <Tooltip.Content>{`Type: ${params.row.mode} | Goal: ${node.value.description}`}</Tooltip.Content>
          </Tooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      renderCell: (params) => <StatusCell value={params.row.status} />,
    },
  ];

  return (
      <DataTable
        rows={interactions}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id ?? row._id}
        disableColumnMenu
        hideFooter
      />
  );
};

